// Group deduplication belongs to the existing presentation; this is display order only.
export function buildFleetPerspectiveIcons(markers = [], contextBadges = [], {
  productLabel = "", productLabels = [], productTitle = "", priorityLabel = "", priorityTitle = "",
} = {}) {
  const products = productLabels.length ? productLabels : productLabel ? [productLabel] : [];
  const statuses = markers.filter(marker => marker?.icon);
  const isProtection = marker => marker.category === "protected"
    || ["protected", "protected-reordered", "reassigned"].includes(marker.key)
    || ["shield", "shuffle"].includes(marker.icon);
  const contexts = contextBadges.filter(badge => (badge?.icon || badge?.label)
    && !(products.length && badge.tone === "product-change"));
  return [
    ...products.map(label => ({ kind: "product", value: { label, title: products.length > 1 ? label : productTitle || label } })),
    ...statuses.filter(isProtection).map(value => ({ kind: "status", value })),
    ...(priorityLabel ? [{ kind: "priority", value: { label: priorityLabel, title: priorityTitle } }] : []),
    ...contexts.filter(badge => badge.tone === "schedule").map(value => ({ kind: "context", value })),
    ...contexts.filter(badge => badge.tone !== "schedule").map(value => ({ kind: "context", value })),
    ...statuses.filter(marker => !isProtection(marker)).map(value => ({ kind: "status", value })),
  ];
}

// Read from the segment upwards: product first. All badge centers share the
// segment's axis, with just one pixel between their fully visible faces.
export function layoutFleetPerspectivePanels(panels, trackWidth = Infinity) {
  const gap = 1;
  const rows = [];
  for (const panel of panels) {
    const previous = rows.at(-1);
    if (panel.rowKey && previous?.rowKey === panel.rowKey && previous.width + gap + panel.width <= trackWidth) {
      previous.panels.push(panel);
      previous.width += gap + panel.width;
      previous.height = Math.max(previous.height, panel.height);
    } else rows.push({ panels: [panel], rowKey: panel.rowKey, width: panel.width, height: panel.height });
  }
  const width = Math.max(0, ...rows.map(row => row.width));
  const height = rows.reduce((sum, row) => sum + row.height, 0) + Math.max(0, rows.length - 1) * gap;
  let bottom = height;
  return {
    panels: rows.flatMap(row => {
      bottom -= row.height;
      let left = (width - row.width) / 2;
      const placed = row.panels.map(panel => {
        const position = { ...panel, x: left, y: bottom + (row.height - panel.height) / 2 };
        left += panel.width + gap;
        return position;
      });
      bottom -= gap;
      return placed;
    }),
    width, height, anchorOffset: width / 2,
  };
}

// Never push a pile sideways over another segment to resolve a collision.
// Keep its foot centered on its own segment; lift only colliding neighbours.
export function layoutFleetPerspectiveStacks(stacks, trackWidth) {
  const gap = 1;
  const sorted = [...stacks].sort((a, b) => a.anchorX - b.anchorX);
  const placed = [];
  for (const stack of sorted) {
    const anchorOffset = stack.anchorOffset ?? stack.width / 2;
    const x = Math.max(0, Math.min(trackWidth - stack.width, stack.anchorX - anchorOffset));
    let lift = 0;
    const neighbours = placed.filter(other => x < other.x + other.width + gap && x + stack.width + gap > other.x)
      .sort((a, b) => a.lift - b.lift);
    for (const other of neighbours) {
      if (lift + stack.height + gap <= other.lift) break;
      lift = Math.max(lift, other.lift + other.height + gap);
    }
    placed.push({ ...stack, x, lift, anchorOffset });
  }
  return placed;
}

export function isFleetPerspectiveTarget(element) {
  return Boolean(element?.closest?.('[data-fleet-perspective="true"]'));
}
